import ExpandableRailList from '../components/ExpandableRailList';

const projects = [
    {
        title: 'First Gen Scholars',
        dates: ['Feb 2026 - Present'],
        subtitle: 'Lead Architectural Engineer',
        blurb: <>
            <p style={{ fontSize: '1.15rem' }}><strong>1stgenapp: EdTech Monolith</strong></p>
            <p>Sole technical engineer for the First Gen Scholars Nonprofit serving 1,000+ students across 22 high schools in San Diego — designing and building the organization's multi-product EdTech platform as a pnpm monorepo, now actively supporting 20+ first-generation college clubs whose students have collectively earned $55.5M in scholarships.</p>
            <p style={{ marginTop: '1rem', fontSize: '1.15rem', paddingLeft: '1.5rem' }}><strong>Applications</strong></p>
            <ul style={{ paddingLeft: '3rem' }}>
                <li><a href="#futurelab-details" onClick={(e) => { e.preventDefault(); document.getElementById('futurelab-details')?.scrollIntoView({ behavior: 'smooth' }); }}><strong>FutureLab</strong></a> <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#2e7d32', border: '1px solid #2e7d32', borderRadius: '4px', padding: '0.1rem 0.4rem', marginLeft: '0.3rem', verticalAlign: 'middle' }}>LIVE</span> — An 8-week curriculum delivery and community research platform where students complete guided lessons, take AI-powered strengths assessments, conduct neighborhood needs surveys, and explore local socioeconomic data to drive community impact projects.</li>
                <li><strong>Org-Wide Admin Dashboard</strong> — Centralized management console for the nonprofit's leadership across all clubs and programs.</li>
                <li><strong>Social Hub</strong> — Networked student accounts enabling cross-club connection and collaboration.</li>
                <li><strong>Storyteller's Studio</strong> — A flagship college-prep program that ingests 1,000+ student applications annually and uses applicant data to match first-generation students to ideal and likely schools based on their interests, passions, and personal narratives.</li>
            </ul>

            <ul style={{ paddingLeft: '1.5rem', marginTop: '1rem' }}>
                <li><strong>Monorepo Architecture</strong> — Built a pnpm monorepo with a Next.js 16 frontend and Fastify 5 REST API, implementing plugin-based server architecture, standardized JSON envelope responses, cursor-based pagination, global error handling (Zod/Prisma/Fastify), and PII-redacting structured logging.</li>
                <li><strong>Auth & RBAC</strong> — Designed a multi-role RBAC authorization system with Firebase JWT verification, auto-provisioning user upsert on first valid token, policy-based access control across 4 roles (student/facilitator/admin/super_admin), and capability-derived UI gating across 3 authenticated portals. Implemented multi-club membership resolution with localStorage persistence, cycle-year scoping, and view-mode switching — a student in one club can be a facilitator in another.</li>
                <li><strong>Database & Schema</strong> — Modeled a 14-table PostgreSQL schema across 12 migration batches with composite unique constraints, cascading deletes, JSONB columns for flexible assessment data, polymorphic upload contexts, and a full application status-workflow state machine.</li>
                <li><strong>File Uploads & Infrastructure</strong> — Built a stream-based file upload pipeline to Google Cloud Storage with multipart parsing, 50MB truncation detection, compensating transactions (GCS cleanup on DB failure and vice versa), and signed URL generation with 15-minute TTL for private-bucket reads.</li>
                <li><strong>DevOps & Deployment</strong> — Deployed frontend to Vercel with custom domain and API to Google Cloud Run with auto-scaling (0–3 instances). Wrote deployment automation scripts with quality gates (typecheck + build before deploy), Cloud SQL Proxy-based database migrations via Docker, post-deploy health verification (/healthz + /readyz), and GCP Secret Manager integration for production secrets.</li>
                <li><strong>Testing</strong> — Architected a 170+ test suite across 14 progressive phases using Vitest, Testing Library, and Node.js native test runner, covering unit, integration, and contract tests with sequential phase dependencies as quality gates.</li>
            </ul>

            <p id="futurelab-details" style={{ marginTop: '1.5rem', fontSize: '1.25rem' }}><strong><a href="https://1stgenfuturelab.org/" target="_blank" rel="noopener noreferrer">FutureLab</a></strong><span style={{ fontSize: '1rem' }}></span></p>
            <p>The organizations's first product — actively used by 260+ students and their facilitators across San Diego.</p>
            <ul style={{ paddingLeft: '1.5rem' }}>
                <li><strong>Curriculum Engine</strong> — Built a 15-lesson curriculum delivery engine with strongly-typed lesson definitions, per-lesson runtime flags, tab-based UI (Lab Instruction, Lab Work, Lab Journal, Exit Ticket), completion tracking, and finalize-lock patterns preventing edits after submission. Shipped a "next action" student dashboard reducing friction for 260+ students navigating a 10-week curriculum.</li>
                <li><strong>AI Integrations</strong> — Integrated OpenAI at two touchpoints: a strengths assessment pipeline scoring 25-question forced-choice assessments across 8 Gardner's Multiple Intelligences roles with personalized coaching profiles, and a community survey summarizer extracting themes from anonymous public responses. Engineered server-side prompt pipelines with system-prompt context injection, regex-based response parsing, and rate limiting (5 req/min).</li>
                <li><strong>Community Survey Platform</strong> — Created a 30-question curated survey bank with shareable QR code/link generation via unique tokens, anonymous public submission (no auth), paginated response viewing, and AI-powered theme summarization — enabling student-led community needs assessments.</li>
                <li><strong>Data Integrations</strong> — Integrated San Diego SDOH indicator datasets (Need Index, unemployment, education, poverty, health/environment scores) and census data into interactive exploration tools, enabling data-driven student community impact research. Mapped career pathway data to strengths assessment results, connecting students' identified roles to San Diego's Priority Sectors.</li>
            </ul>
        </>,
    },
    {
        title: 'Congestion Control Algorithm Research',
        date: 'Sep 2024 - May 2025',
        subtitle: 'Computer Networks Research Lab',
        blurb: (
            <p>
                Scripted cloud-lab experiments measuring how propagation delays and RTTs affect bandwidth fairness
                across competing Congestion Control Algorithms (CCAs), contributing to a novel approach where all
                flows emulate the same underlying propagation delay to remove inherent RTT sensitivity.
                Work contributed to the published paper{' '}
                <a
                    href="https://drops.dagstuhl.de/entities/document/10.4230/OASIcs.NINeS.2026.27"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <em>Making Congestion Control Algorithms Insensitive to Underlying Propagation Delays</em>
                </a>{' '}
                (NINes 2026). Research conducted under{' '}
                <a
                    href="https://www.cs.columbia.edu/~danr/"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    Prof. Dan Rubenstein
                </a>,{' '}
                <a
                    href="https://www.cs.columbia.edu/~misra/"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    Prof. Vishal Misra
                </a>, and{' '}
                <a
                    href="https://www.linkedin.com/in/cyrus-illick-0a9627186/"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    Cyrus Illick
                </a>, PhD Candidate
            </p>
        )
        ,
    },
    {
        title: 'crackd.ai: Humor and Generative AI Research',
        date: 'Jan 2025 - May 2025',
        subtitle: 'Generative AI Research Lab',
        blurb: (
            <p>
                Co-designed fine-tuning pipeline and data backend, launching an app that
                collects user data to iteratively train a humour-generating model.
                {' '}
                Research and development completed under{' '}
                <a
                    href="https://www.cs.columbia.edu/~chilton/chilton.html"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    Prof. Lydia Chilton
                </a>.
                <br/>
                <br />
                <a
                    href="https://crackd.ai/"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    See the app
                </a>
            </p>
        )

    },
    {
        title: 'Columbia University',
        date: 'Sep 2021 - May 2025',
        subtitle: (
            <p>
                Columbia College
                <br />
                B.A. in Computer Science
            </p>
        ),
        blurb: <>
            <p>GPA: 3.7</p>
            <p>Relevant Coursework:</p>
            <ul style={{ paddingLeft: '1.5rem' }}>
                <li>Data Structures and Algorithms</li>
                <li>Design and Analysis of Algorithms</li>
                <li>Systems Programming</li>
                <li>Open Source Development</li>
                <li>Clean Object-Oriented Design</li>
                <li>Database Systems</li>
                <li>UI Design</li>
                <li>Design For Generative AI</li>
                <li>Artificial Intelligence</li>
                <li>iOS Mobile Development</li>
                <li>Computer Architecture</li>
                <li>Complexity Theory</li>
                <li>Science of Blockchains</li>
            </ul>
        </>,
    },
];

export default function Projects() {
    return (
        <div className='experience experience--compact'>
            <ExpandableRailList items={projects} header="Projects, Research, and my Education" />
        </div>

    );
}
